import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "horizontal" | "compact";
}

function formatViews(views: number) {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  const to = `/detail/${article.id}`;

  if (variant === "horizontal") {
    return (
      <Link
        to={to}
        className="group flex flex-col sm:flex-row gap-4 bg-background-50 rounded-lg border border-background-200 p-3 md:p-4 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-full sm:w-48 lg:w-56 h-44 sm:h-32 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 黑人文化资讯`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
              {article.categoryName}
            </span>
            {article.hot && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 whitespace-nowrap">
                热门
              </span>
            )}
          </div>
          <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.summary}</p>
          <div className="mt-auto pt-3 flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
            <span>{article.author}</span>
            <span className="w-1 h-1 rounded-full bg-background-400" />
            <span>{article.date}</span>
            <span className="w-1 h-1 rounded-full bg-background-400" />
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link to={to} className="group block py-3 border-b border-background-200 last:border-0 cursor-pointer">
        <div className="flex items-start gap-3">
          <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-800 flex items-center justify-center text-xs font-medium shrink-0 mt-0.5">
            {article.hot ? <i className="ri-fire-line text-accent-600" /> : <i className="ri-article-line" />}
          </span>
          <div className="min-w-0">
            <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors leading-snug line-clamp-2">
              {article.title}
            </h4>
            <div className="mt-1 flex items-center gap-2 text-xs text-foreground-400">
              <span>{article.categoryName}</span>
              <span>{article.date}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={to}
      className="group flex flex-col bg-background-50 rounded-lg border border-background-200 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer h-full"
    >
      <div className="w-full h-48 md:h-52 overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 黑人文化资讯`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex-1 flex flex-col p-4 md:p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
            {article.categoryName}
          </span>
          {article.editorPick && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 whitespace-nowrap">
              编辑精选
            </span>
          )}
        </div>
        <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug line-clamp-2">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-3">{article.summary}</p>
        <div className="mt-auto pt-4 flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
          <span>{article.author}</span>
          <span className="w-1 h-1 rounded-full bg-background-400" />
          <span>{article.date}</span>
          <span className="w-1 h-1 rounded-full bg-background-400" />
          <span className="flex items-center gap-1">
            <i className="ri-time-line" />
            {article.readTime} 分钟
          </span>
        </div>
      </div>
    </Link>
  );
}